import lottie, { AnimationItem } from 'lottie-web';
import { useEffect, useRef, useState } from 'react';

import { Center, useColorMode } from '@vimeo/bokeh';
import { _3Layers, Clock, Comment, DownloadImport, Flag, Heart, Send } from '@vimeo/bokeh-icons';
import { bokehTheme } from '@vimeo/bokeh-theme';

const iconMap = {
  send: Send,
  comment: Comment,
  '3-layers': _3Layers,
  clock: Clock,
  'download-import': DownloadImport,
  flag: Flag,
  heart: Heart,
};

export const HoverAnimatedIcon = ({ iconName }: { iconName: keyof typeof iconMap }) => {
  const lottieRef = useRef<AnimationItem | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { colorMode } = useColorMode();
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (containerRef.current) {
      if (lottieRef.current) {
        lottieRef.current.destroy();
      }
      lottieRef.current = lottie.loadAnimation({
        container: containerRef?.current as Element,
        loop: false,
        autoplay: false,
        path: `https://f.vimeocdn.com/motion/bokeh_${iconName}_${colorMode}.json`,
      });
    }
  }, [iconName, colorMode]);

  const IconComponent = iconMap[iconName];

  useEffect(() => {
    let parentButton = containerRef.current?.closest('.chakra-button');
    if (!parentButton) {
      return () => {
        // do nothing
      };
    }

    let onParentEnter = () => {
      setIsHovered(true);
      lottieRef.current?.play();
    };
    let onParentLeave = () => {
      setIsHovered(false);
      lottieRef.current?.stop();
    };

    parentButton.addEventListener('mouseenter', onParentEnter);
    parentButton.addEventListener('mouseleave', onParentLeave);

    return () => {
      // This is always set if we're here but TS complains if we don't check
      if (parentButton) {
        parentButton.removeEventListener('mouseenter', onParentEnter);
        parentButton.removeEventListener('mouseleave', onParentLeave);
      }
    };
  }, []);

  return (
    <Center
      position="relative"
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore-next-line svg is unknown to typescript. 20px is the current value of that width field in bokeh-theme, but I would rather not use a magic number.
      boxSize={bokehTheme.components.Button.sizes?.sm?.svg?.width ?? '20px'}
    >
      <Center display={isHovered ? 'none' : 'flex'}>
        <IconComponent />
      </Center>
      <Center ref={containerRef} display={isHovered ? 'flex' : 'none'} />
    </Center>
  );
};
